/**
 * The emoji catalogue behind the picker.
 *
 * Deliberately hand-kept rather than pulled from a full Unicode dataset: the
 * complete set is a megabyte of JSON to render a grid nobody scrolls to the
 * bottom of, and the long tail is reachable through search on the names here.
 *
 * `TONEABLE` lists the entries that take a skin-tone modifier. It is a set of
 * the base characters rather than a flag per entry so `applySkinTone` can be
 * asked about any emoji, including one pasted in from elsewhere.
 */

export interface EmojiEntry {
  char: string;
  /** Search terms — the picker matches on any of them. */
  keywords: string[];
}

export interface EmojiCategory {
  id: string;
  label: string;
  emoji: EmojiEntry[];
}

const e = (char: string, ...keywords: string[]): EmojiEntry => ({ char, keywords });

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'people',
    label: 'Smileys & people',
    emoji: [
      e('😀', 'grin', 'happy', 'smile'),
      e('😃', 'smiley', 'happy'),
      e('😄', 'laugh', 'happy'),
      e('😁', 'beam', 'grin'),
      e('😆', 'laughing', 'lol'),
      e('😅', 'sweat', 'relief', 'phew'),
      e('🤣', 'rofl', 'lol', 'laughing'),
      e('😂', 'joy', 'tears', 'lol'),
      e('🙂', 'slight', 'smile'),
      e('🙃', 'upside down', 'irony'),
      e('😉', 'wink'),
      e('😊', 'blush', 'smile'),
      e('😇', 'innocent', 'halo', 'angel'),
      e('😍', 'heart eyes', 'love'),
      e('🤩', 'star struck', 'wow'),
      e('😘', 'kiss'),
      e('😗', 'kissing'),
      e('😋', 'yum', 'tasty'),
      e('😜', 'wink tongue', 'silly'),
      e('🤪', 'zany', 'goofy'),
      e('🤨', 'raised eyebrow', 'sceptical', 'skeptical'),
      e('🧐', 'monocle', 'inspect'),
      e('🤓', 'nerd', 'geek'),
      e('😎', 'cool', 'sunglasses'),
      e('🥳', 'party', 'celebrate'),
      e('😏', 'smirk'),
      e('😒', 'unamused', 'meh'),
      e('😞', 'disappointed', 'sad'),
      e('😔', 'pensive', 'sad'),
      e('😟', 'worried'),
      e('🙁', 'frown', 'sad'),
      e('😣', 'persevere', 'struggle'),
      e('😖', 'confounded'),
      e('😫', 'tired', 'exhausted'),
      e('😩', 'weary'),
      e('🥺', 'pleading', 'puppy eyes'),
      e('😢', 'cry', 'sad', 'tear'),
      e('😭', 'sob', 'crying'),
      e('😤', 'triumph', 'huff'),
      e('😠', 'angry', 'mad'),
      e('😡', 'rage', 'furious'),
      e('🤯', 'mind blown', 'exploding head'),
      e('😳', 'flushed', 'embarrassed'),
      e('🥵', 'hot', 'overheated'),
      e('🥶', 'cold', 'freezing'),
      e('😱', 'scream', 'shock'),
      e('😨', 'fearful', 'scared'),
      e('😰', 'anxious', 'nervous'),
      e('🤔', 'thinking', 'hmm'),
      e('🤫', 'shush', 'quiet', 'secret'),
      e('🤐', 'zipper', 'silence'),
      e('😴', 'sleep', 'zzz'),
      e('🤤', 'drool'),
      e('🤢', 'nauseated', 'sick'),
      e('🤮', 'vomit', 'sick'),
      e('🤧', 'sneeze', 'ill'),
      e('😷', 'mask', 'sick'),
      e('🤒', 'thermometer', 'ill'),
      e('🤕', 'bandage', 'hurt'),
      e('🥱', 'yawn', 'bored'),
      e('😐', 'neutral', 'meh'),
      e('😶', 'no mouth', 'speechless'),
      e('🙄', 'eye roll'),
      e('😬', 'grimace', 'awkward'),
      e('🤥', 'lying', 'pinocchio'),
      e('👶', 'baby'),
      e('🧑', 'person'),
      e('👨', 'man'),
      e('👩', 'woman'),
      e('🧓', 'older person'),
      e('👮', 'police', 'officer'),
      e('🕵️', 'detective', 'spy'),
      e('👷', 'construction', 'worker'),
      e('🧑‍💻', 'developer', 'coder', 'engineer'),
      e('🧑‍🔬', 'scientist'),
      e('🧑‍🚀', 'astronaut'),
      e('🦸', 'superhero'),
      e('🧙', 'wizard', 'mage'),
      e('🤦', 'facepalm'),
      e('🤷', 'shrug', 'dunno'),
      e('💁', 'information', 'sassy'),
      e('🙋', 'raising hand', 'volunteer'),
      e('🙇', 'bow', 'sorry'),
      e('🕺', 'dancing man'),
      e('💃', 'dancing woman'),
      e('🏃', 'run'),
      e('🚶', 'walk'),
    ],
  },
  {
    id: 'gestures',
    label: 'Gestures',
    emoji: [
      e('👍', 'thumbs up', 'yes', 'approve', '+1'),
      e('👎', 'thumbs down', 'no', '-1'),
      e('👌', 'ok', 'perfect'),
      e('🤌', 'pinched fingers', 'italian'),
      e('✌️', 'peace', 'victory'),
      e('🤞', 'crossed fingers', 'luck', 'hope'),
      e('🤟', 'love you'),
      e('🤘', 'rock on', 'horns'),
      e('🤙', 'call me', 'shaka'),
      e('👈', 'point left'),
      e('👉', 'point right'),
      e('👆', 'point up'),
      e('👇', 'point down'),
      e('☝️', 'index up', 'one'),
      e('✋', 'raised hand', 'stop'),
      e('🤚', 'back of hand'),
      e('🖐️', 'splayed hand'),
      e('🖖', 'vulcan', 'spock'),
      e('👋', 'wave', 'hello', 'bye'),
      e('🤝', 'handshake', 'deal', 'agree'),
      e('👏', 'clap', 'applause', 'bravo'),
      e('🙌', 'raised hands', 'celebrate', 'praise'),
      e('👐', 'open hands', 'hug'),
      e('🤲', 'palms up'),
      e('🙏', 'pray', 'thanks', 'please'),
      e('✍️', 'writing', 'note'),
      e('💪', 'muscle', 'strong', 'flex'),
      e('🦾', 'mechanical arm', 'robot'),
      e('👊', 'fist bump', 'punch'),
      e('✊', 'raised fist'),
      e('🫰', 'finger heart'),
      e('🫡', 'salute', 'yes sir'),
      e('🫶', 'heart hands'),
    ],
  },
  {
    id: 'nature',
    label: 'Animals & nature',
    emoji: [
      e('🐶', 'dog', 'puppy'),
      e('🐱', 'cat', 'kitten'),
      e('🐭', 'mouse'),
      e('🐹', 'hamster'),
      e('🐰', 'rabbit', 'bunny'),
      e('🦊', 'fox'),
      e('🐻', 'bear'),
      e('🐼', 'panda'),
      e('🐨', 'koala'),
      e('🐯', 'tiger'),
      e('🦁', 'lion'),
      e('🐮', 'cow'),
      e('🐷', 'pig'),
      e('🐸', 'frog'),
      e('🐵', 'monkey'),
      e('🙈', 'see no evil'),
      e('🐔', 'chicken'),
      e('🐧', 'penguin'),
      e('🐦', 'bird'),
      e('🦆', 'duck'),
      e('🦅', 'eagle'),
      e('🦉', 'owl'),
      e('🐝', 'bee'),
      e('🐛', 'bug', 'caterpillar'),
      e('🦋', 'butterfly'),
      e('🐌', 'snail', 'slow'),
      e('🐞', 'ladybug', 'bug'),
      e('🕷️', 'spider'),
      e('🐢', 'turtle', 'slow'),
      e('🐍', 'snake', 'python'),
      e('🦖', 'dinosaur', 't-rex'),
      e('🐙', 'octopus'),
      e('🦑', 'squid'),
      e('🦀', 'crab', 'rust'),
      e('🐠', 'fish'),
      e('🐬', 'dolphin'),
      e('🐳', 'whale'),
      e('🦈', 'shark'),
      e('🐊', 'crocodile'),
      e('🐘', 'elephant'),
      e('🦒', 'giraffe'),
      e('🐴', 'horse'),
      e('🌵', 'cactus'),
      e('🌲', 'tree', 'evergreen'),
      e('🌳', 'tree'),
      e('🌴', 'palm tree'),
      e('🌱', 'seedling', 'sprout', 'growth'),
      e('🍀', 'clover', 'luck'),
      e('🌿', 'herb', 'leaf'),
      e('🍁', 'maple leaf', 'autumn'),
      e('🌸', 'cherry blossom'),
      e('🌺', 'hibiscus'),
      e('🌻', 'sunflower'),
      e('🌹', 'rose'),
      e('🌷', 'tulip'),
      e('🌍', 'earth', 'globe', 'world'),
      e('🌞', 'sun'),
      e('🌙', 'moon', 'night'),
      e('⭐', 'star'),
      e('🌟', 'glowing star', 'sparkle'),
      e('✨', 'sparkles', 'magic', 'shiny'),
      e('⚡', 'lightning', 'fast', 'zap'),
      e('🔥', 'fire', 'hot', 'lit'),
      e('💧', 'droplet', 'water'),
      e('🌊', 'wave', 'ocean'),
      e('❄️', 'snowflake', 'cold'),
      e('🌈', 'rainbow'),
      e('☁️', 'cloud'),
      e('🌧️', 'rain'),
    ],
  },
  {
    id: 'food',
    label: 'Food & drink',
    emoji: [
      e('🍏', 'apple', 'green'),
      e('🍎', 'apple', 'red'),
      e('🍐', 'pear'),
      e('🍊', 'orange', 'tangerine'),
      e('🍋', 'lemon'),
      e('🍌', 'banana'),
      e('🍉', 'watermelon'),
      e('🍇', 'grapes'),
      e('🍓', 'strawberry'),
      e('🫐', 'blueberries'),
      e('🍒', 'cherries'),
      e('🍑', 'peach'),
      e('🥭', 'mango'),
      e('🍍', 'pineapple'),
      e('🥥', 'coconut'),
      e('🥑', 'avocado'),
      e('🍅', 'tomato'),
      e('🌶️', 'chilli', 'spicy'),
      e('🌽', 'corn'),
      e('🥕', 'carrot'),
      e('🥔', 'potato'),
      e('🍞', 'bread'),
      e('🥐', 'croissant'),
      e('🥨', 'pretzel'),
      e('🧀', 'cheese'),
      e('🥚', 'egg'),
      e('🍳', 'cooking', 'fried egg'),
      e('🥞', 'pancakes'),
      e('🥓', 'bacon'),
      e('🍔', 'burger', 'hamburger'),
      e('🍟', 'fries', 'chips'),
      e('🍕', 'pizza'),
      e('🌭', 'hot dog'),
      e('🥪', 'sandwich'),
      e('🌮', 'taco'),
      e('🌯', 'burrito'),
      e('🥗', 'salad'),
      e('🍝', 'pasta', 'spaghetti'),
      e('🍜', 'ramen', 'noodles'),
      e('🍣', 'sushi'),
      e('🍤', 'shrimp', 'tempura'),
      e('🍚', 'rice'),
      e('🍰', 'cake', 'slice'),
      e('🎂', 'birthday cake'),
      e('🧁', 'cupcake'),
      e('🍪', 'cookie'),
      e('🍩', 'doughnut', 'donut'),
      e('🍫', 'chocolate'),
      e('🍬', 'candy', 'sweet'),
      e('🍿', 'popcorn'),
      e('☕', 'coffee', 'tea', 'hot drink'),
      e('🍵', 'green tea'),
      e('🧉', 'mate'),
      e('🥤', 'soft drink', 'soda'),
      e('🧋', 'bubble tea'),
      e('🍺', 'beer'),
      e('🍻', 'cheers', 'beers'),
      e('🥂', 'clink', 'celebrate', 'champagne'),
      e('🍷', 'wine'),
      e('🥃', 'whisky', 'whiskey'),
      e('🍾', 'bottle', 'pop', 'celebrate'),
    ],
  },
  {
    id: 'activity',
    label: 'Activity & travel',
    emoji: [
      e('⚽', 'football', 'soccer'),
      e('🏀', 'basketball'),
      e('🏈', 'american football'),
      e('⚾', 'baseball'),
      e('🎾', 'tennis'),
      e('🏐', 'volleyball'),
      e('🏓', 'ping pong', 'table tennis'),
      e('🏸', 'badminton'),
      e('🥅', 'goal'),
      e('⛳', 'golf'),
      e('🎣', 'fishing'),
      e('🥊', 'boxing'),
      e('🏋️', 'weight lifting', 'gym'),
      e('🤸', 'cartwheel'),
      e('🧘', 'yoga', 'meditation'),
      e('🏄', 'surfing'),
      e('🏊', 'swimming'),
      e('🚴', 'cycling', 'bike'),
      e('🧗', 'climbing'),
      e('🏆', 'trophy', 'win'),
      e('🥇', 'gold medal', 'first'),
      e('🥈', 'silver medal', 'second'),
      e('🥉', 'bronze medal', 'third'),
      e('🎯', 'target', 'bullseye', 'goal'),
      e('🎮', 'game', 'controller'),
      e('🕹️', 'joystick', 'arcade'),
      e('🎲', 'dice', 'random'),
      e('♟️', 'chess', 'pawn'),
      e('🎸', 'guitar'),
      e('🎹', 'piano', 'keyboard'),
      e('🎤', 'microphone', 'sing'),
      e('🎧', 'headphones', 'music'),
      e('🎬', 'clapper', 'film', 'action'),
      e('🎨', 'art', 'palette', 'design'),
      e('🚗', 'car'),
      e('🚕', 'taxi'),
      e('🚌', 'bus'),
      e('🚲', 'bicycle'),
      e('🛴', 'scooter'),
      e('✈️', 'plane', 'flight', 'travel'),
      e('🚀', 'rocket', 'launch', 'ship it'),
      e('🛸', 'ufo'),
      e('🚁', 'helicopter'),
      e('⛵', 'sailboat'),
      e('🚢', 'ship'),
      e('🚂', 'train'),
      e('🏝️', 'island', 'holiday', 'vacation'),
      e('🏕️', 'camping'),
      e('🗺️', 'map'),
      e('🧳', 'luggage', 'travel'),
    ],
  },
  {
    id: 'objects',
    label: 'Objects',
    emoji: [
      e('💻', 'laptop', 'computer'),
      e('🖥️', 'desktop', 'monitor'),
      e('⌨️', 'keyboard'),
      e('🖱️', 'mouse'),
      e('🖨️', 'printer'),
      e('📱', 'phone', 'mobile'),
      e('☎️', 'telephone'),
      e('📷', 'camera'),
      e('🎥', 'video camera'),
      e('📺', 'tv', 'television'),
      e('🔋', 'battery'),
      e('🔌', 'plug', 'power'),
      e('💡', 'idea', 'bulb', 'light'),
      e('🔦', 'torch', 'flashlight'),
      e('🕯️', 'candle'),
      e('📡', 'satellite', 'signal'),
      e('💾', 'floppy', 'save'),
      e('💿', 'disc'),
      e('🗄️', 'file cabinet', 'archive'),
      e('📁', 'folder'),
      e('📂', 'open folder'),
      e('📄', 'document', 'page'),
      e('📋', 'clipboard'),
      e('📌', 'pin', 'pushpin'),
      e('📎', 'paperclip', 'attach'),
      e('✂️', 'scissors', 'cut'),
      e('📏', 'ruler'),
      e('🔍', 'search', 'magnifying glass'),
      e('🔒', 'lock', 'private', 'secure'),
      e('🔓', 'unlock', 'open'),
      e('🔑', 'key', 'password'),
      e('🔨', 'hammer', 'build'),
      e('🛠️', 'tools', 'fix'),
      e('🔧', 'wrench', 'config'),
      e('⚙️', 'gear', 'settings'),
      e('🧰', 'toolbox'),
      e('🧪', 'test tube', 'experiment'),
      e('🔬', 'microscope', 'research'),
      e('💊', 'pill', 'medicine'),
      e('🩹', 'bandage', 'patch', 'fix'),
      e('🛒', 'cart', 'shopping'),
      e('💰', 'money bag'),
      e('💳', 'card', 'payment'),
      e('💎', 'gem', 'diamond'),
      e('⏰', 'alarm', 'clock', 'time'),
      e('⏳', 'hourglass', 'waiting'),
      e('📅', 'calendar', 'date'),
      e('📈', 'chart up', 'growth'),
      e('📉', 'chart down', 'decline'),
      e('📊', 'bar chart', 'stats', 'poll'),
      e('🗓️', 'spiral calendar'),
      e('📖', 'book', 'read', 'docs'),
      e('📚', 'books', 'library'),
      e('✏️', 'pencil', 'edit'),
      e('🖊️', 'pen'),
      e('🗑️', 'bin', 'trash', 'delete'),
      e('🎁', 'gift', 'present'),
      e('🎉', 'party popper', 'celebrate', 'tada'),
      e('🎊', 'confetti'),
      e('🏅', 'medal'),
      e('🔔', 'bell', 'notification'),
      e('🔕', 'muted bell', 'silence'),
      e('📢', 'announcement', 'loudspeaker'),
      e('📣', 'megaphone', 'shout'),
    ],
  },
  {
    id: 'symbols',
    label: 'Symbols',
    emoji: [
      e('❤️', 'heart', 'love', 'red'),
      e('🧡', 'orange heart'),
      e('💛', 'yellow heart'),
      e('💚', 'green heart'),
      e('💙', 'blue heart'),
      e('💜', 'purple heart'),
      e('🖤', 'black heart'),
      e('🤍', 'white heart'),
      e('💔', 'broken heart'),
      e('💯', 'hundred', 'perfect', 'score'),
      e('✅', 'check', 'done', 'tick', 'yes'),
      e('☑️', 'ballot check'),
      e('✔️', 'check mark'),
      e('❌', 'cross', 'no', 'wrong'),
      e('❎', 'cross mark button'),
      e('⚠️', 'warning', 'caution'),
      e('🚫', 'prohibited', 'no entry'),
      e('❓', 'question'),
      e('❗', 'exclamation'),
      e('💤', 'zzz', 'sleep'),
      e('💬', 'speech', 'comment'),
      e('💭', 'thought'),
      e('🗯️', 'anger bubble'),
      e('👀', 'eyes', 'look', 'watching'),
      e('🧠', 'brain', 'smart'),
      e('🦴', 'bone'),
      e('🩸', 'blood'),
      e('♻️', 'recycle'),
      e('🔄', 'refresh', 'sync', 'loop'),
      e('🔼', 'up'),
      e('🔽', 'down'),
      e('➡️', 'right arrow'),
      e('⬅️', 'left arrow'),
      e('🔀', 'shuffle'),
      e('▶️', 'play'),
      e('⏸️', 'pause'),
      e('⏹️', 'stop'),
      e('⏺️', 'record'),
      e('🔊', 'loud', 'volume'),
      e('🔇', 'mute'),
      e('➕', 'plus', 'add'),
      e('➖', 'minus', 'remove'),
      e('✖️', 'multiply'),
      e('➗', 'divide'),
      e('🟢', 'green circle', 'online'),
      e('🟡', 'yellow circle', 'away'),
      e('🔴', 'red circle', 'busy', 'recording'),
      e('🔵', 'blue circle'),
      e('⚪', 'white circle'),
      e('⚫', 'black circle'),
      e('🟥', 'red square'),
      e('🟩', 'green square'),
      e('🟦', 'blue square'),
      e('#️⃣', 'hash', 'channel'),
      e('🆗', 'ok button'),
      e('🆕', 'new'),
      e('🔝', 'top'),
      e('🈵', 'full'),
    ],
  },
  {
    id: 'flags',
    label: 'Flags',
    emoji: [
      e('🏁', 'chequered', 'finish', 'race'),
      e('🚩', 'triangular flag', 'flagged'),
      e('🏳️', 'white flag', 'surrender'),
      e('🏴', 'black flag'),
      e('🏳️‍🌈', 'rainbow flag', 'pride'),
      e('🏴‍☠️', 'pirate flag'),
      e('🇺🇳', 'united nations'),
      e('🇪🇺', 'european union'),
      e('🇬🇧', 'united kingdom', 'uk', 'britain'),
      e('🇺🇸', 'united states', 'usa', 'america'),
      e('🇨🇦', 'canada'),
      e('🇧🇷', 'brazil'),
      e('🇲🇽', 'mexico'),
      e('🇫🇷', 'france'),
      e('🇩🇪', 'germany'),
      e('🇪🇸', 'spain'),
      e('🇮🇹', 'italy'),
      e('🇳🇱', 'netherlands'),
      e('🇵🇱', 'poland'),
      e('🇸🇪', 'sweden'),
      e('🇳🇴', 'norway'),
      e('🇺🇦', 'ukraine'),
      e('🇹🇷', 'turkey'),
      e('🇪🇬', 'egypt'),
      e('🇸🇦', 'saudi arabia'),
      e('🇦🇪', 'united arab emirates', 'uae'),
      e('🇳🇬', 'nigeria'),
      e('🇿🇦', 'south africa'),
      e('🇰🇪', 'kenya'),
      e('🇮🇳', 'india'),
      e('🇵🇰', 'pakistan'),
      e('🇧🇩', 'bangladesh'),
      e('🇨🇳', 'china'),
      e('🇯🇵', 'japan'),
      e('🇰🇷', 'south korea'),
      e('🇸🇬', 'singapore'),
      e('🇮🇩', 'indonesia'),
      e('🇦🇺', 'australia'),
      e('🇳🇿', 'new zealand'),
    ],
  },
];

/**
 * Emoji that accept a skin-tone modifier. Applying one to an emoji that does
 * not take it produces a stray swatch character, so the picker checks here
 * before offering the tone strip.
 */
export const TONEABLE = new Set<string>([
  '🧑', '👨', '👩', '👶', '🧓', '👮', '🕵️', '👷', '🧑‍💻', '🧑‍🔬', '🧑‍🚀',
  '🦸', '🧙', '🤦', '🤷', '💁', '🙋', '🙇', '🕺', '💃', '🏃', '🚶',
  '👍', '👎', '👌', '🤌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆',
  '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '👏', '🙌', '👐', '🤲', '🙏',
  '✍️', '💪', '👊', '✊', '🫰', '🫡', '🫶',
  '🏋️', '🤸', '🧘', '🏄', '🏊', '🚴', '🧗',
]);

/** The one-click reactions offered on a message, the same set everywhere. */
export const QUICK_REACTIONS = ['👍', '🎉', '❤️', '😂', '👀', '🚀'];

/** Recently used emoji, kept per browser rather than per account. */
const RECENT_KEY = 'itsappy.recentEmoji';
const RECENT_LIMIT = 32;

export function recentEmoji(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function rememberEmoji(char: string): string[] {
  const next = [char, ...recentEmoji().filter((c) => c !== char)].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // A full or blocked localStorage is not worth failing a reaction over.
  }
  return next;
}

const TONE_KEY = 'itsappy.skinTone';

export function storedSkinTone(): string {
  try {
    return localStorage.getItem(TONE_KEY) ?? 'default';
  } catch {
    return 'default';
  }
}

export function storeSkinTone(tone: string): void {
  try {
    localStorage.setItem(TONE_KEY, tone);
  } catch {
    // Same as above — a preference that fails to persist is not an error.
  }
}

export function searchEmoji(query: string): EmojiEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results: EmojiEntry[] = [];
  for (const category of EMOJI_CATEGORIES) {
    for (const entry of category.emoji) {
      if (entry.keywords.some((k) => k.includes(q))) results.push(entry);
    }
  }
  return results.slice(0, 60);
}
